import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../components/AuthLayout';
import { useAuth } from '../auth/AuthProvider';
import { BrandMark, PoweredBy } from '../branding/Branding';
import { TextField } from '../components/fields';
import { PasswordField } from '../branding/PasswordField';
import { FormError } from '../components/states';

export function LoginPage() {
  const { login, notice } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/agencies';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const secondFactor = needsMfa ? (useRecovery ? { recoveryCode: recoveryCode.trim() } : { mfaCode }) : {};
      const result = await login({ email, password, ...secondFactor });
      if (result.status === 'mfa_required') {
        setNeedsMfa(true);
        return;
      }
      navigate(from, { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout>
      <BrandMark />
      <form className="card form" onSubmit={onSubmit} aria-labelledby="login-title">
        <h1 id="login-title">Sign in</h1>
        {notice ? (
          <p className="notice" role="status">
            {notice}
          </p>
        ) : null}
        <TextField label="Email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <PasswordField label="Password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        {needsMfa && !useRecovery ? (
          <TextField
            label="Authentication code"
            hint="Enter the 6-digit code from your authenticator app."
            inputMode="numeric"
            autoComplete="one-time-code"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
          />
        ) : null}
        {needsMfa && useRecovery ? (
          <TextField
            label="Recovery code"
            hint="One of the codes you saved when you turned on two-step verification."
            autoComplete="off"
            required
            value={recoveryCode}
            onChange={(e) => setRecoveryCode(e.target.value)}
          />
        ) : null}
        {needsMfa ? (
          <button type="button" className="link-button" onClick={() => setUseRecovery((v) => !v)}>
            {useRecovery ? 'Use a code from your app instead' : 'Use a recovery code instead'}
          </button>
        ) : null}
        <FormError error={error} />
        <button type="submit" className="button button-primary" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
        <p className="form-footer">
          <Link to="/forgot-password">Forgot your password?</Link>
        </p>
        <p className="form-footer">
          New here? <Link to="/register">Create an account</Link>
        </p>
      </form>
      <PoweredBy />
    </AuthLayout>
  );
}
